import type { ThemeId } from '../types.js';

export interface Theme {
  id: ThemeId;
  label: string;
  /** Picker section: Nature / City / Abstract (setup screen groups by this). */
  group: 'nature' | 'city' | 'abstract';
  skyTop: string;
  skyBottom: string;
  hillBack: string;
  hillFront: string;
  grass: string;
  grassDark: string;
  dirt: string;
  dirtDark: string;
  speckle: string;
  water: string;
  waterDeep: string;
  /** Prop densities: average spawn probability per 26px column. */
  treeDensity: number;
  flowerDensity: number;
  rockDensity: number;
  bushDensity: number;
  potDensity: number;
  /** Street lamps (city-family maps) — optional so nature themes needn't set it. */
  lampDensity?: number;
  /** Glowing crystal shards (abstract maps) — optional for the same reason. */
  crystalDensity?: number;
  caveChance: number;
  waterChance: number;
  /** Terrain shaping. */
  relief: number;
  roughness: number;
  /**
   * Snap the height profile to plateaus of this many px (0/undefined = off) —
   * flat mesa tops with sheer cliff faces between them.
   */
  terraces?: number;
}

export const THEMES: Record<ThemeId, Theme> = {
  meadow: {
    id: 'meadow',
    label: 'Meadow',
    group: 'nature',
    skyTop: '#79c7ea',
    skyBottom: '#d8f3dc',
    hillBack: '#a5d6a7',
    hillFront: '#7cb98a',
    grass: '#5fbf5a',
    grassDark: '#3f9147',
    dirt: '#8a6244',
    dirtDark: '#5d4130',
    speckle: '#a07a56',
    water: '#4fa8dd',
    waterDeep: '#2f6fa8',
    treeDensity: 0.22,
    flowerDensity: 0.5,
    rockDensity: 0.12,
    bushDensity: 0.14,
    potDensity: 0.06,
    caveChance: 0.5,
    waterChance: 0.45,
    relief: 1,
    roughness: 1,
  },
  forest: {
    id: 'forest',
    label: 'Forest',
    group: 'nature',
    skyTop: '#5fa8c7',
    skyBottom: '#bfe3c9',
    hillBack: '#5f8f6a',
    hillFront: '#41704f',
    grass: '#3f9e54',
    grassDark: '#2b713d',
    dirt: '#6f5138',
    dirtDark: '#463322',
    speckle: '#876847',
    water: '#4b9fd0',
    waterDeep: '#2a6a9c',
    treeDensity: 0.5,
    flowerDensity: 0.28,
    rockDensity: 0.16,
    bushDensity: 0.26,
    potDensity: 0.05,
    caveChance: 0.75,
    waterChance: 0.4,
    relief: 1.1,
    roughness: 1.15,
  },
  desert: {
    id: 'desert',
    label: 'Desert',
    group: 'nature',
    skyTop: '#8fd0ef',
    skyBottom: '#ffe8c2',
    hillBack: '#eccb8f',
    hillFront: '#dcb577',
    grass: '#d9b36a',
    grassDark: '#b8914e',
    dirt: '#c99a5b',
    dirtDark: '#8f6a3b',
    speckle: '#e4c083',
    water: '#57b6d8',
    waterDeep: '#2f88ad',
    treeDensity: 0.04,
    flowerDensity: 0.1,
    rockDensity: 0.34,
    bushDensity: 0.08,
    potDensity: 0.14,
    caveChance: 0.3,
    waterChance: 0.12,
    relief: 0.7,
    roughness: 0.65,
  },
  snow: {
    id: 'snow',
    label: 'Snowfall',
    group: 'nature',
    skyTop: '#a9c9e8',
    skyBottom: '#eef6ff',
    hillBack: '#cfdce9',
    hillFront: '#aebfd0',
    grass: '#eef5fb',
    grassDark: '#c9d8e6',
    dirt: '#7e8794',
    dirtDark: '#565e69',
    speckle: '#9aa5b2',
    water: '#7fc4e8',
    waterDeep: '#4a90bd',
    treeDensity: 0.24,
    flowerDensity: 0.05,
    rockDensity: 0.2,
    bushDensity: 0.1,
    potDensity: 0.05,
    caveChance: 0.4,
    waterChance: 0.25,
    relief: 1.15,
    roughness: 0.85,
  },
  city: {
    id: 'city',
    label: 'City Blocks',
    group: 'city',
    skyTop: '#5f9fd4',
    skyBottom: '#ffd9a6',
    hillBack: '#9aa3b2',
    hillFront: '#7d8493',
    grass: '#8b93a3', // street surface — the "grass band" reads as asphalt edge
    grassDark: '#6b7280',
    dirt: '#5f6570', // concrete below the pavement
    dirtDark: '#3f444d',
    speckle: '#7a8190',
    water: '#4d9ad0',
    waterDeep: '#2c6a9a',
    treeDensity: 0.12, // street trees in the sidewalks
    flowerDensity: 0.06,
    rockDensity: 0.04,
    bushDensity: 0.1,
    potDensity: 0.3, // planters
    caveChance: 0.12, // the odd under-street tunnel
    waterChance: 0.14, // a canal through town
    relief: 0.22, // streets are flat
    roughness: 0.35,
  },
  canyon: {
    id: 'canyon',
    label: 'Red Canyon',
    group: 'nature',
    skyTop: '#7ec8e8',
    skyBottom: '#ffd9b0',
    hillBack: '#d98e5f',
    hillFront: '#c47345',
    grass: '#a3a35c', // dry scrub on the mesa tops
    grassDark: '#7f8143',
    dirt: '#b0603c',
    dirtDark: '#7c4126',
    speckle: '#d08a5c',
    water: '#4fa0c8',
    waterDeep: '#2f6f96',
    treeDensity: 0.06,
    flowerDensity: 0.05,
    rockDensity: 0.4,
    bushDensity: 0.12,
    potDensity: 0.02,
    caveChance: 0.55, // arches and hollows
    waterChance: 0.3, // the river at the bottom
    relief: 1.7, // the vertical one
    roughness: 1.25,
    terraces: 64, // mesas: flat tops, sheer drops
  },
  highlands: {
    id: 'highlands',
    label: 'Highlands',
    group: 'nature',
    skyTop: '#6bb6dd',
    skyBottom: '#dff0e8',
    hillBack: '#7fa06a',
    hillFront: '#5d7f52',
    grass: '#6fae5e',
    grassDark: '#4c8143',
    dirt: '#7d6a4f',
    dirtDark: '#544733',
    speckle: '#94805f',
    water: '#4d9ecf',
    waterDeep: '#2b6f9e',
    treeDensity: 0.18,
    flowerDensity: 0.2,
    rockDensity: 0.3,
    bushDensity: 0.2,
    potDensity: 0.03,
    caveChance: 0.5,
    waterChance: 0.35, // hill lochs
    relief: 1.6,
    roughness: 0.9,
    terraces: 76, // steep-sided plateaus
  },
  islands: {
    id: 'islands',
    label: 'Islands',
    group: 'nature',
    skyTop: '#6ec6ef',
    skyBottom: '#d7f6f3',
    hillBack: '#8fd0a8',
    hillFront: '#67b98a',
    grass: '#56c177',
    grassDark: '#3a9159',
    dirt: '#9b7a55',
    dirtDark: '#6b5238',
    speckle: '#b28f66',
    water: '#3fb6d8',
    waterDeep: '#1f7fa8',
    treeDensity: 0.2,
    flowerDensity: 0.3,
    rockDensity: 0.15,
    bushDensity: 0.12,
    potDensity: 0.04,
    caveChance: 0.35,
    waterChance: 0.9, // water everywhere — landings are separated by sea
    relief: 1.2,
    roughness: 1.3,
  },
  harbor: {
    id: 'harbor',
    label: 'Harbor',
    group: 'nature',
    skyTop: '#6fb0d6',
    skyBottom: '#ffe0b8',
    hillBack: '#9aa7b5',
    hillFront: '#7c8797',
    grass: '#8a8168', // weathered quayside stone
    grassDark: '#6b6452',
    dirt: '#6e5f4c',
    dirtDark: '#4a4034',
    speckle: '#877963',
    water: '#3f9ec9',
    waterDeep: '#22688f',
    treeDensity: 0.06,
    flowerDensity: 0.05,
    rockDensity: 0.12,
    bushDensity: 0.06,
    potDensity: 0.1,
    caveChance: 0.1,
    waterChance: 1, // the whole middle of the map is one working bay
    relief: 0.35,
    roughness: 0.5,
  },

  // --- Nature (+1): warm shallows -------------------------------------------
  lagoon: {
    id: 'lagoon',
    label: 'Lagoon',
    group: 'nature',
    skyTop: '#7ed6ea',
    skyBottom: '#eafaf4',
    hillBack: '#56bfae',
    hillFront: '#2f9e8f',
    grass: '#74cf79',
    grassDark: '#48a85c',
    dirt: '#d8c48c',
    dirtDark: '#b39a61',
    speckle: '#fff3d2',
    water: '#3ad2c4',
    waterDeep: '#1290aa',
    treeDensity: 0.1,
    flowerDensity: 0.06,
    rockDensity: 0.05,
    bushDensity: 0.07,
    potDensity: 0.02,
    caveChance: 0.12,
    waterChance: 0.55, // keys and shallows — the bay is the point
    relief: 0.55,
    roughness: 0.6,
  },

  // --- City (+2): downtown by day and by night ------------------------------
  skyline: {
    id: 'skyline',
    label: 'Skyline',
    group: 'city',
    skyTop: '#90bfe8',
    skyBottom: '#e9f2fb',
    hillBack: '#93a6bf',
    hillFront: '#6f829c',
    grass: '#87909e', // asphalt — the "grass band" is the street surface
    grassDark: '#68707c',
    dirt: '#5f6672',
    dirtDark: '#414650',
    speckle: '#7c8492',
    water: '#4d8ec9',
    waterDeep: '#2c5f96',
    treeDensity: 0.1,
    flowerDensity: 0.04,
    rockDensity: 0.03,
    bushDensity: 0.08,
    potDensity: 0.34, // planters
    lampDensity: 0.12, // street lamps
    caveChance: 0.08,
    waterChance: 0.12, // the odd canal
    relief: 0.24, // streets are flat
    roughness: 0.38,
  },
  neon: {
    id: 'neon',
    label: 'Neon District',
    group: 'city',
    skyTop: '#1a0b33',
    skyBottom: '#45165c',
    hillBack: '#2e1650',
    hillFront: '#1e1038',
    grass: '#41316c', // night asphalt
    grassDark: '#2c2252',
    dirt: '#4d3c72',
    dirtDark: '#342754',
    speckle: '#c08cff',
    water: '#22d8d0',
    waterDeep: '#0b7f99',
    treeDensity: 0.04,
    flowerDensity: 0.03,
    rockDensity: 0.04,
    bushDensity: 0.06,
    potDensity: 0.2,
    lampDensity: 0.16, // every block glows
    caveChance: 0.06,
    waterChance: 0.12,
    relief: 0.26,
    roughness: 0.42,
  },

  // --- Abstract (+9): color-field maps --------------------------------------
  candy: {
    id: 'candy',
    label: 'Candy Fields',
    group: 'abstract',
    skyTop: '#ffd7ee',
    skyBottom: '#fff4d8',
    hillBack: '#ffb6d4',
    hillFront: '#e892c0',
    grass: '#ffdbec',
    grassDark: '#f2abcb',
    dirt: '#cba5e2',
    dirtDark: '#9d78bd',
    speckle: '#fff7fc',
    water: '#8ed8f6',
    waterDeep: '#59a7e1',
    treeDensity: 0.04,
    flowerDensity: 0.18,
    rockDensity: 0.06,
    bushDensity: 0.1,
    potDensity: 0.04,
    caveChance: 0.2,
    waterChance: 0.16,
    relief: 0.65,
    roughness: 0.55,
  },
  sunset: {
    id: 'sunset',
    label: 'Sunset Ridge',
    group: 'abstract',
    skyTop: '#ff9a56',
    skyBottom: '#ffd9a3',
    hillBack: '#e06a5a',
    hillFront: '#b84b5a',
    grass: '#f2a65e',
    grassDark: '#cf7f45',
    dirt: '#8f4f63',
    dirtDark: '#6b3a4f',
    speckle: '#ffd9b3',
    water: '#ff8f6b',
    waterDeep: '#c9556b',
    treeDensity: 0.05,
    flowerDensity: 0.08,
    rockDensity: 0.14,
    bushDensity: 0.08,
    potDensity: 0.04,
    caveChance: 0.14,
    waterChance: 0.14,
    relief: 1.15,
    roughness: 0.7,
    terraces: 48, // dusk mesas in bands
  },
  void: {
    id: 'void',
    label: 'Void',
    group: 'abstract',
    skyTop: '#05060f',
    skyBottom: '#141b38',
    hillBack: '#111530',
    hillFront: '#0a0d20',
    grass: '#272c48',
    grassDark: '#181c32',
    dirt: '#1d2140',
    dirtDark: '#11141f',
    speckle: '#8fa2ff',
    water: '#1c3d72',
    waterDeep: '#0a1a3d',
    treeDensity: 0.02,
    flowerDensity: 0,
    rockDensity: 0.07,
    bushDensity: 0.02,
    potDensity: 0,
    crystalDensity: 0.12,
    caveChance: 0.25,
    waterChance: 0.1,
    relief: 0.9,
    roughness: 0.75,
  },
  prism: {
    id: 'prism',
    label: 'Prism',
    group: 'abstract',
    skyTop: '#241b5e',
    skyBottom: '#b8f0ff',
    hillBack: '#4a3f9e',
    hillFront: '#7a5fd0',
    grass: '#a8d8ff',
    grassDark: '#7fb3e8',
    dirt: '#c8b8ff',
    dirtDark: '#9a86d8',
    speckle: '#ffffff',
    water: '#59d8ff',
    waterDeep: '#2a9fd8',
    treeDensity: 0.03,
    flowerDensity: 0.06,
    rockDensity: 0.08,
    bushDensity: 0.06,
    potDensity: 0,
    crystalDensity: 0.1,
    caveChance: 0.18,
    waterChance: 0.15,
    relief: 0.85,
    roughness: 0.7,
  },
  magma: {
    id: 'magma',
    label: 'Magma Core',
    group: 'abstract',
    skyTop: '#2c0f0a',
    skyBottom: '#75270f',
    hillBack: '#4c1d13',
    hillFront: '#36140c',
    grass: '#4e2620', // basalt crust
    grassDark: '#351714',
    dirt: '#6b3120',
    dirtDark: '#431d12',
    speckle: '#ff7a2f', // glowing embers in the rock
    water: '#2f8f9b', // cold springs — drown water must read as water, not safe lava
    waterDeep: '#14555f',
    treeDensity: 0,
    flowerDensity: 0,
    rockDensity: 0.12,
    bushDensity: 0.04,
    potDensity: 0,
    crystalDensity: 0.07,
    caveChance: 0.55, // lava tubes
    waterChance: 0.08,
    relief: 1.25,
    roughness: 0.95,
  },
  mint: {
    id: 'mint',
    label: 'Mint Plateau',
    group: 'abstract',
    skyTop: '#c9f7ea',
    skyBottom: '#f3fffa',
    hillBack: '#91e2ca',
    hillFront: '#5ec6aa',
    grass: '#a0e9ca',
    grassDark: '#6ec5a6',
    dirt: '#8bbcae',
    dirtDark: '#5f9284',
    speckle: '#eafff7',
    water: '#5fd8d0',
    waterDeep: '#2f9fa8',
    treeDensity: 0.05,
    flowerDensity: 0.08,
    rockDensity: 0.06,
    bushDensity: 0.12,
    potDensity: 0.06,
    caveChance: 0.1,
    waterChance: 0.2,
    relief: 0.7,
    roughness: 0.45,
    terraces: 56, // soft plateaus
  },
  golden: {
    id: 'golden',
    label: 'Golden Prairie',
    group: 'abstract',
    skyTop: '#ffda8c',
    skyBottom: '#fff7e2',
    hillBack: '#e9b65c',
    hillFront: '#cb9140',
    grass: '#f1c660',
    grassDark: '#d09e40',
    dirt: '#b9824b',
    dirtDark: '#8e6034',
    speckle: '#fff1c6',
    water: '#7fc4e0',
    waterDeep: '#4a92c4',
    treeDensity: 0.05,
    flowerDensity: 0.16,
    rockDensity: 0.08,
    bushDensity: 0.1,
    potDensity: 0.03,
    caveChance: 0.15,
    waterChance: 0.12,
    relief: 0.75,
    roughness: 0.55,
  },
  orchid: {
    id: 'orchid',
    label: 'Orchid Hollow',
    group: 'abstract',
    skyTop: '#6d42bb',
    skyBottom: '#eeabe2',
    hillBack: '#7f51c2',
    hillFront: '#5b3698',
    grass: '#b37cda',
    grassDark: '#8b57ba',
    dirt: '#6f4b98',
    dirtDark: '#4e3372',
    speckle: '#f2d6ff',
    water: '#9c6ce2',
    waterDeep: '#603bab',
    treeDensity: 0.04,
    flowerDensity: 0.1,
    rockDensity: 0.07,
    bushDensity: 0.08,
    potDensity: 0.03,
    crystalDensity: 0.12,
    caveChance: 0.28,
    waterChance: 0.12,
    relief: 0.95,
    roughness: 0.7,
  },
  mono: {
    id: 'mono',
    label: 'Monochrome',
    group: 'abstract',
    skyTop: '#e6e6e6',
    skyBottom: '#fafafa',
    hillBack: '#bdbdbd',
    hillFront: '#909090',
    grass: '#e1e1e1',
    grassDark: '#bebebe',
    dirt: '#8c8c8c',
    dirtDark: '#5d5d5d',
    speckle: '#ffffff',
    water: '#c9d5dd',
    waterDeep: '#7f919e',
    treeDensity: 0.02,
    flowerDensity: 0,
    rockDensity: 0.09,
    bushDensity: 0.03,
    potDensity: 0.03,
    caveChance: 0.2,
    waterChance: 0.1,
    relief: 0.9,
    roughness: 0.7,
  },
};

export const THEME_IDS: ThemeId[] = [
  'meadow',
  'forest',
  'desert',
  'snow',
  'city',
  'canyon',
  'highlands',
  'islands',
  'harbor',
  'lagoon',
  'skyline',
  'neon',
  'candy',
  'sunset',
  'void',
  'prism',
  'magma',
  'mint',
  'golden',
  'orchid',
  'mono',
];
